import React, { useState, useEffect } from 'react';
import Loader from '../../components/Common/Loader';
import SectionsManager from '../../components/Admin/SectionsManager';
import Modal from '../../components/Common/Modal';
import { getCourses, createCourse, updateCourse, deleteCourse } from '../../services/adminService';
import { confirmDialog } from '../../components/Common/confirm';
import toast from 'react-hot-toast';

const IcoBook = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);
const IcoCap = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"/>
  </svg>
);
const IcoLayers = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>
  </svg>
);
// Иконка по названию курса
const iconFor = (name = '') =>
  /ОГЭ/i.test(name) ? IcoBook : /ЕГЭ/i.test(name) ? IcoCap : IcoLayers;

const SectionsPage = () => {
  const [courses, setCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);       // null = создание
  const [nameValue, setNameValue] = useState('');
  const [examValue, setExamValue] = useState('ОГЭ'); // ОГЭ | ЕГЭ
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async (selectId) => {
    try {
      const res = await getCourses();
      const list = res.data || [];
      setCourses(list);
      setActiveCourse(prev => selectId || (list.some(c => c.id === prev) ? prev : list[0]?.id) || null);
    } catch { toast.error('Не удалось загрузить курсы'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditId(null); setNameValue(''); setExamValue('ОГЭ'); setModalOpen(true); };
  const openRename = () => {
    const c = courses.find(x => x.id === activeCourse);
    if (!c) return;
    setEditId(c.id); setNameValue(c.name);
    setExamValue(c.examType || (/ЕГЭ/i.test(c.name) ? 'ЕГЭ' : 'ОГЭ'));
    setModalOpen(true);
  };

  const handleSave = async () => {
    const name = nameValue.trim();
    if (!name) return toast.error('Введите название курса');
    if (!examValue) return toast.error('Выберите экзамен (ОГЭ/ЕГЭ)');
    setSaving(true);
    try {
      if (editId) {
        await updateCourse(editId, { name, examType: examValue });
        toast.success('Курс обновлён');
        await load(editId);
      } else {
        const res = await createCourse({ name, examType: examValue });
        toast.success('Курс добавлен');
        await load(res.data?.id);
      }
      setModalOpen(false);
    } catch (e) { toast.error(e.response?.data?.message || 'Ошибка'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const c = courses.find(x => x.id === activeCourse);
    if (!c) return;
    if (!(await confirmDialog({ title: 'Удалить курс?', message: `Курс «${c.name}» вместе с его разделами и уроками будет удалён. Действие необратимо.`, confirmText: 'Удалить' }))) return;
    try {
      await deleteCourse(c.id);
      toast.success('Курс удалён');
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Не удалось удалить курс'); }
  };

  return (
    <div style={{ maxWidth: 1180 }}>
      <div style={{ marginBottom: 22 }}>
        <h1 className="s-page-title">Разделы и уроки</h1>
        <p className="s-page-sub">Структура курса · разделы, уроки, материалы</p>
      </div>

      {loading ? (
        <Loader />
      ) : courses.length === 0 ? (
        <div className="pg-empty">Курсов пока нет. Нажмите «Добавить курс», чтобы создать первый.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            {courses.map((c) => {
              const Icon = iconFor(c.examType || c.name);
              const active = activeCourse === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveCourse(c.id)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '9px 20px', borderRadius: 30,
                    border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                    fontSize: 13, fontWeight: active ? 700 : 500,
                    cursor: 'pointer', fontFamily: 'inherit',
                    background: active ? 'var(--primary)' : 'var(--card)',
                    color: active ? '#fff' : 'var(--text-2)',
                    boxShadow: active ? '0 6px 16px -6px rgba(124,77,255,0.6)' : 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  <Icon />
                  {c.name}
                </button>
              );
            })}
            {/* Добавить курс / переименовать / удалить */}
            <button onClick={openCreate} title="Добавить курс"
              style={{ marginLeft: 4, background: 'transparent', border: '1px solid var(--primary)', borderRadius: 9, width: 34, height: 34, cursor: 'pointer', color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
            <button onClick={openRename} title="Переименовать курс"
              style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 9, width: 34, height: 34, cursor: 'pointer', color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
            <button onClick={handleDelete} title="Удалить курс"
              style={{ background: 'transparent', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 9, width: 34, height: 34, cursor: 'pointer', color: '#dc2626', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </button>
          </div>

          {activeCourse && <SectionsManager courseId={activeCourse} />}
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editId ? 'Переименовать курс' : 'Новый курс'} size="small">
        <div className="form-group">
          <label className="form-label">Название курса</label>
          <input
            value={nameValue}
            onChange={e => setNameValue(e.target.value)}
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
            style={{ width: '100%', padding: '11px 13px', borderRadius: 11, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text-1)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }}
          />
        </div>
        <div className="form-group" style={{ marginTop: 14 }}>
          <label className="form-label">Экзамен</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['ОГЭ', 'ЕГЭ'].map(ex => {
              const active = examValue === ex;
              return (
                <button
                  key={ex}
                  type="button"
                  onClick={() => setExamValue(ex)}
                  style={{
                    flex: 1, padding: '10px 12px', borderRadius: 11, cursor: 'pointer', fontFamily: 'inherit',
                    fontSize: 13.5, fontWeight: 700,
                    border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                    background: active ? 'var(--primary)' : 'var(--card)',
                    color: active ? '#fff' : 'var(--text-2)',
                    transition: 'all 0.15s',
                  }}
                >{ex}</button>
              );
            })}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
            Курсов может быть много (месячный, годовой, интенсив…), но каждый привязан к ОГЭ или ЕГЭ.
          </p>
        </div>
        <div className="form-actions" style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="action-btn secondary" onClick={() => setModalOpen(false)}>Отмена</button>
          <button className="action-btn purple" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохранение...' : (editId ? 'Сохранить' : 'Добавить')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SectionsPage;
